import React from "react";
import queryString from "query-string";
import { endpoints } from "../../..";
import { FeedbackAutocompleteItem } from "../../../../Layout/Models/AutocompleteItem";
import { LoadingState, LoadingDisplay, ErrorDisplay, ApiRequest } from "../../../../Common";
import SimilarFeedbackSuggestions from "./SimilarFeedbackSuggestions";
import { FeedbackParent } from "../../../Constants/feedbackEnums";
import { Application } from "../../../../App";
import { Topic } from "../../../../Topic/Models/Topic";
import ResponseHandler from "../../../../App/ResponseHandler";
import { ResponseResult } from "../../../../Common/Models/ResponseResult";

type Props = {
  query: string;
  selectedParent: FeedbackParent;
  selectedApp?: Application;
  selectedTopic?: Topic;
};

type State = {
  loadedSuggestionsForQuery?: string;
  loadedSuggestions: FeedbackAutocompleteItem[];
  loadingState: LoadingState;
  errorCode?: number;
  errorDescription?: string;
};

export default class SimilarFeedbackSuggestionsContainer extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loadedSuggestions: [],
      loadingState: {
        isLoaded: false,
        isLoading: false,
        errored: false
      }
    };
  }

  componentDidMount() {
    this.loadIfNeeded();
  }

  componentDidUpdate() {
    this.loadIfNeeded();
  }

  loadIfNeeded() {
    if (
      this.props.query !== this.state.loadedSuggestionsForQuery &&
      !this.state.loadingState.errored &&
      !this.state.loadingState.isLoading &&
      ((this.props.selectedParent === FeedbackParent.Application && !!this.props.selectedApp) ||
        (this.props.selectedParent === FeedbackParent.Topic && !!this.props.selectedTopic))
    ) {
      this.load(this.props.query);
    }
  }

  makeGetFeedbacksAutocompleteUrl() {
    let url: string = endpoints.feedback.autocompleteSearch();

    // get the current context
    let context: string = "";
    let contextItemId: string = "";

    if (this.props.selectedParent === FeedbackParent.Application) {
      context = "App";
      contextItemId = this.props.selectedApp!.rolePrefix;
    } else if (this.props.selectedParent === FeedbackParent.Topic) {
      context = "Topic";
      contextItemId = this.props.selectedTopic!.code;
    }

    const queryArgs = {
      query: this.props.query,
      maxSuggestionsToGet: 5,
      context,
      contextItemId
    };

    const stringified = queryString.stringify(queryArgs);

    url += `?${stringified}`;

    return url;
  }

  load(query: string) {
    const setState = this.setState.bind(this);

    // set the state to loading before we make the api request
    setState(
      (prevState: State) => ({
        loadedSuggestionsForQuery: query,
        loadedSuggestions: [],
        loadingState: {
          isLoading: true,
          isLoaded: false,
          errored: false
        }
      }),
      // make API request on the setState callback
      () => {
        ApiRequest.fromPath(this.makeGetFeedbacksAutocompleteUrl())
          .withAuthHeader()
          .get()
          .then((response: Response) => {
            ResponseHandler.handle(
              response,
              (result: ResponseResult) => {
                setState((prevState: State) => ({
                  loadedSuggestions: result.payload,
                  loadingState: {
                    isLoading: false,
                    isLoaded: true,
                    errored: false
                  }
                }));
              },
              (result: ResponseResult) => {
                setState((prevState: State) => ({
                  errorCode: response.status,
                  errorDescription: result.error.message,
                  loadingState: {
                    isLoading: false,
                    isLoaded: false,
                    errored: true
                  }
                }));
              },
              () => {
                setState((prevState: State) => ({
                  errorCode: response.status,
                  errorDescription: "An unknown error has occured",
                  loadingState: {
                    isLoading: false,
                    isLoaded: false,
                    errored: true
                  }
                }));
              }
            );
          });
      }
    );
  }

  render() {
    if (this.state.loadingState.errored) {
      return (
        <ErrorDisplay
          errorResult="Could not load similar feedback suggestions"
          errorDescription={this.state.errorDescription}
          errorCode={this.state.errorCode}
        />
      );
    }

    if (this.state.loadingState.isLoading) {
      return <LoadingDisplay />;
    }

    return (
      <SimilarFeedbackSuggestions query={this.props.query} items={this.state.loadedSuggestions} />
    );
  }
}
